import { useState } from 'react';
import {
  IroTimeHorizonPickerFragment,
  TimeHorizon,
  useUpdateIroTimeHorizonMutation,
} from '@/graphql/generated';
import { useToast } from '@/components/layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n';
import { GradientDropdownStrength } from '@/components/generic/dropdown/GradientDropdown';
import { useRefetchMaterialities } from './useRefetchMaterialities';
import { IroPickerDropdown } from '@/components/project/iro/evaluation/picker/IroPickerDropdown';

export type IroTimeHorizonItem = {
  id: TimeHorizon | null;
  name: string;
  strength: GradientDropdownStrength;
};

const timeHorizonCriteriaChoicesMap = (): Map<
  TimeHorizon | null,
  IroTimeHorizonItem
> =>
  new Map([
    [
      TimeHorizon.ShortTerm,
      {
        id: TimeHorizon.ShortTerm,
        name: i18n.t(`enum:iroCriteria.timeHorizon.${TimeHorizon.ShortTerm}`),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      TimeHorizon.MediumTerm,
      {
        id: TimeHorizon.MediumTerm,
        name: i18n.t(`enum:iroCriteria.timeHorizon.${TimeHorizon.MediumTerm}`),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      TimeHorizon.LongTerm,
      {
        id: TimeHorizon.LongTerm,
        name: i18n.t(`enum:iroCriteria.timeHorizon.${TimeHorizon.LongTerm}`),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      null,
      {
        id: null,
        name: '-',
        strength: GradientDropdownStrength.VeryLight,
      },
    ],
  ]);

export const timeHorizonCriteriaChoices = () =>
  Array.from(timeHorizonCriteriaChoicesMap().values());

export const IroTimeHorizonPicker = ({
  iro,
  readOnly = false,
}: {
  iro: IroTimeHorizonPickerFragment;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const items = timeHorizonCriteriaChoicesMap();
  const initialTimeHorizonItem = items.get(iro.timeHorizon || null);
  const [selected, setSelected] = useState(initialTimeHorizonItem || null);
  const [update] = useUpdateIroTimeHorizonMutation();
  const toast = useToast();
  const refetchQueries = useRefetchMaterialities();

  const chooseItemId = (itemId: string | null) => {
    const item = items.get(itemId as TimeHorizon | null) || null;
    setSelected(item);

    update({
      variables: {
        input: {
          id: iro.id,
          timeHorizon: item?.id ?? null,
        },
      },
      refetchQueries,
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:iro.criteria.update.success', {
            criteriaName: t('iro.criteria.timeHorizon.title'),
          }),
        );
      })
      .catch(() => {
        toast.openToastWithError(
          t('toast:iro.criteria.timeHorizon.update.error'),
        );
      });
  };

  return (
    <IroPickerDropdown<IroTimeHorizonItem>
      items={items}
      selected={selected}
      chooseItemId={chooseItemId}
      assessmentStats={iro.assessmentStats?.timeHorizon ?? null}
      readOnly={readOnly}
    />
  );
};
